// ── V149: 오늘의 작업 탭 ──────────────────────────────────────────────────────
// 사장님: "오늘의 작업을 딱 한눈에 보기 좋게 클릭해서 만들고, 체크박스도 만들고,
//          작업한 리스트를 발주서처럼 저장하면 좋겠다"
//   · 왼쪽(모바일은 '+ 작업 추가' 창): 레시피를 눌러 추가 · 직접 입력(냉장고 정리 같은 일)
//   · 오른쪽: 오늘 목록 — 큰 체크박스, 만들 양(g / 인분), 그 양에 맞춘 재료
//   · 💾 저장 → 날짜별 내역 (발주서 내역처럼). 내역에서 다시 오늘 목록으로 불러올 수 있다
//   · 체크는 누가·언제 했는지 같이 남는다. 여러 직원이 동시에 체크해도 서로 안 지워진다 (useFirestore.setWorkCheck)
// 데이터 (users/{매장}): workToday { date, items[] } · workChecks { 항목id: { done, at, by } } · workHistory [ ... ]
// ── V155 ── "순서 변경 가능하게 · 작업 / 회의 주제 · 내용 · 기한 · 담당자 · 위챗 공유 (발주서처럼)"
//   · ↕ 순서 바꾸기: ▲▼ · 맨 위로 · (PC) 끌어서 놓기
//   · 직접 적는 항목은 구분(🔧 작업 / 💬 회의 주제)을 고른다. 모든 항목에 내용·기한·담당자 (✎로 고침)
//   · 목록 위에서 구분·담당자별로 걸러 보기. 기한이 지난 일은 빨간색
//   · 💬 위챗: 한국어+중국어 글을 만들어 휴대폰은 공유창, 컴퓨터는 복사 (utils/workShare.js)
//   · 저장할 때 안 끝난 항목은 새 목록에 남길 수 있다
// ── V156 ── "순서 변경이 적용 안 됐고 · 미완료는 다음 날 다시 · 완료는 저장 · 회의는 따로 (회의 탭)"
//   · 맨 위 탭: 📋 오늘의 작업 | 🗓 회의 (components/MeetingPane.js — 전체 화면)
//   · 순서: 줄 왼쪽 ⠿ 손잡이를 끌어서 (손가락·마우스 모두, pointer 이벤트) + ↕ 순서 (▲▼ · 맨 위로)
//   · 날이 바뀌면 다 한 작업은 내역에 저장, 안 한 작업은 그대로 남는다 (utils/workRollover.js, App에서도 돈다)
//   · 💾 완료 저장 = 다 한 것만 내역으로 (안 한 건 남음)
//   · '회의 주제' 구분은 없앴다. 회의에서 넘어온 테스크는 🗓 배지 (누르면 그 회의)
//   · 마감일은 날짜만, 담당자는 칩으로 고르기 (components/WorkFields.js)
// ── V157 ── "체크박스 누르면 누른 날짜 기준으로 자동 저장되고 목록에서 사라지게"
//   · 체크 = 그날 내역에 저장 (체크한 시각의 날짜). 목록에는 안 한 작업만 보인다. 잘못 눌렀으면 '되돌리기'
//   · 저장 자체는 체크 칸(workChecks, 칸별 merge)만 바꾼다 → 직원 여럿이 동시에 눌러도 서로 안 지워진다
//     내역 화면은 저장된 내역 + 체크해 둔 작업을 날짜별로 합쳐 보여주고, 날이 바뀌면 정식으로 옮긴다 (workRollover)
//   · '💾 완료 저장' 버튼은 없앴다
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLang } from '../i18n/LangContext';
import BiName, { buildZhMap } from '../i18n/BiName';
import { printWorkList } from '../utils/workPrint';
import {
  todayKey, newWorkId, fmtDay, recipeBase, scaleIngredients, fmtNum, fmtAmount, sumIngredients,
} from '../utils/workCalc';
import {
  applyMeta, fmtDue, dueState, ownerSuggestions, moveItem, NOTE_MAX, TASK_MAX,
} from '../utils/workMeta';
import { buildWorkText } from '../utils/workShare';
import { archiveDone, needsRollover } from '../utils/workRollover';
import { normMeeting, upsertMeeting, applyMeetingToWork, removeMeetingFromWork } from '../utils/meetingLink';
import MeetingPane from './MeetingPane';
import { OwnerPicker, DuePicker, FieldRow, textareaStyle, shareText } from './WorkFields';

const ORANGE = '#FF6F0F';
const MULTS = [0.5, 1, 2, 3];
const MULT_LABEL = { 0.5: '×½', 1: '×1', 2: '×2', 3: '×3' };
const GREEN = '#07C160';                 // 위챗 버튼 (발주서와 같은 색)
const MEET = { color: '#1D4ED8', bg: '#EFF6FF' };   // 회의에서 온 테스크 배지

const hhmm = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// 처음 넣을 때의 양 — 레시피 기준량(한 번 만드는 양)
function defaultAmount(r) {
  const base = recipeBase(r);
  const sv = Number(r?.serving_g) || 0;
  const mode = r?.type === 'menu' && sv > 0 ? 'serv' : 'g';
  return { grams: base > 0 ? Math.round(base * 100) / 100 : (sv > 0 ? sv : 0), mode };
}

export default function WorkToday({
  categories = [], prices = [], user,
  workToday = { date: '', items: [] }, workChecks = {}, workHistory = [],
  onWorkTodayChange, onCheck, onSaveHistory, onDeleteHistory,
  isMobile = false, storeName = '', employees = [],
  meetings = [], onCommitWork, autoRollover = true,   // V156 회의 · 날 넘기기 (App이 넘기면 autoRollover=false)
}) {
  const { lang } = useLang();
  const zh = lang === 'zh';
  const Z = (ko, z) => (zh ? z : ko);
  const today = todayKey();

  // ── 레시피 ────────────────────────────────────────────────────────────────
  const recipes = useMemo(() => (categories || []).flatMap(c => (c?.items || []).map(it => ({ ...it, _cat: c }))), [categories]);
  const recipesById = useMemo(() => new Map(recipes.filter(r => r && r.id).map(r => [r.id, r])), [recipes]);
  const semiByName = useMemo(() => {
    const m = new Map();
    for (const r of recipes) if (r?.type === 'semi' && r.name) m.set(String(r.name).replace(/\s+/g, ''), r);
    return m;
  }, [recipes]);
  const zhMap = useMemo(() => buildZhMap(prices || [], recipes), [prices, recipes]);

  const items = Array.isArray(workToday?.items) ? workToday.items : [];
  const listDate = workToday?.date || '';
  const stale = items.length > 0 && listDate && listDate !== today && !onCommitWork;   // V156: 넘기기를 못 할 때만 옛 안내
  const isDone = (id) => !!(workChecks && workChecks[id] && workChecks[id].done);
  const doneCount = items.filter(i => isDone(i.id)).length;
  // V157: 목록에 보이는 건 안 한 작업만. 다 한 작업은 아래 '다 한 작업' 칸과 내역에
  const active = items.filter(i => !isDone(i.id));
  const doneItems = items.filter(i => isDone(i.id))
    .sort((a, b) => (Number(workChecks[b.id]?.at) || 0) - (Number(workChecks[a.id]?.at) || 0));

  // ── 화면 상태 ─────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('today');             // V156 'today' | 'meeting'
  const [openMeetingId, setOpenMeetingId] = useState(null);
  const [view, setView] = useState('today');           // 'today' | 'history'
  const [pickerOpen, setPickerOpen] = useState(false);  // 모바일 '+ 작업 추가' 창
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');  // all | semi | menu
  const [text, setText] = useState('');
  const [openIngs, setOpenIngs] = useState({});
  const [drafts, setDrafts] = useState({});             // 양 입력 중인 글자 (항목id → 문자열)
  const [showTotal, setShowTotal] = useState(false);
  const [showDone, setShowDone] = useState(false);   // V157 다 한 작업 펼치기
  const [printDlg, setPrintDlg] = useState(null);   // V154 { items, date, memo } — 뺄 작업 고르고 인쇄
  const [shareDlg, setShareDlg] = useState(null);   // V155 { items, date, memo, off } — 위챗으로 보낼 글
  const [addMore, setAddMore] = useState(false);     // 담당자 · 마감일 · 내용 칸 펼침
  const [addMeta, setAddMeta] = useState({ note: '', owner: '', due: '' });
  const [editId, setEditId] = useState(null);        // ✎ 고치는 항목
  const [reorder, setReorder] = useState(false);     // ↕ 순서 바꾸기
  const [drag, setDrag] = useState(null);           // V156 { id, overId } ⠿ 끌기
  const dragRef = useRef(null);
  const rootRef = useRef(null);
  const [srcFilter, setSrcFilter] = useState('all'); // all | meeting (회의에서 온 테스크)
  const [ownerFilter, setOwnerFilter] = useState(''); // '' 전체 · '-' 담당 없음 · 이름
  const [toast, setToast] = useState(null);           // { msg, undo? }
  const toastTimer = useRef(null);
  const flash = (msg, undo = null) => {
    setToast({ msg, undo });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), undo ? 6000 : 2500);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // ── 목록 바꾸기 ───────────────────────────────────────────────────────────
  const commit = (nextItems, date) => {
    if (!onWorkTodayChange) return;
    // 비어 있던 목록에 처음 넣을 때는 오늘 날짜로 시작
    const d = date || ((items.length === 0 || !listDate) ? today : listDate);
    onWorkTodayChange({ date: d, items: nextItems });
  };

  const addRecipe = (r, gramsOverride) => {
    if (!r) return;
    const { grams, mode } = defaultAmount(r);
    const it = {
      id: newWorkId(), kind: 'recipe', recipeId: r.id, name: r.name || '',
      ...(r.name_zh ? { name_zh: r.name_zh } : {}),
      grams: gramsOverride != null ? Math.round(gramsOverride * 10) / 10 : grams, mode,
    };
    commit([...items, it]);
    flash(Z(`'${r.name}' 추가했어요`, `已添加「${r.name_zh || r.name}」`));
  };

  const addText = () => {
    const t = text.trim();
    if (!t) return;
    const it = applyMeta({ id: newWorkId(), kind: 'text', text: t.slice(0, TASK_MAX) }, addMeta);
    commit([...items, it]);
    setText('');
    setAddMeta(m => ({ note: '', owner: m.owner, due: m.due }));   // 담당자·날짜는 이어서 쓰기 좋게 남긴다
    if (addMeta.owner || addMeta.due || addMeta.note) {
      flash(Z(`'${t.slice(0, 20)}' 추가했어요`, `已添加「${t.slice(0, 20)}」`));
    }
  };

  const patchItem = (id, patch) => commit(items.map(i => (i.id === id ? { ...i, ...patch } : i)));
  const removeItem = (id) => { if (editId === id) setEditId(null); commit(items.filter(i => i.id !== id)); };

  // V155 ✎ 고치기 — 구분 · 이름(직접 적은 항목) · 내용 · 기한 · 담당자
  const saveEdit = (id, v) => {
    commit(items.map(i => {
      if (i.id !== id) return i;
      let n = i.kind === 'text' ? { ...i, text: (String(v.text || '').trim() || i.text).slice(0, TASK_MAX) } : { ...i };
      n = applyMeta(n, v);
      return n;
    }));
    setEditId(null);
  };

  // V155 ↕ 순서
  const move = (id, to) => {
    const from = items.findIndex(i => i.id === id);
    if (from < 0) return;
    commit(moveItem(items, from, to));
  };
  // V156 ⠿ 끌어서 놓기 — pointer 이벤트라 손가락(태블릿·휴대폰)과 마우스 모두 된다
  //   손잡이에 pointer capture → 움직일 때 손가락 아래 줄(data-work-row)을 찾고, 놓으면 그 자리로
  const scrollParent = () => {
    let el = rootRef.current && rootRef.current.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const st = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (st && /(auto|scroll)/.test(st.overflowY || '') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  };
  // 손가락이 화면 위·아래 끝에 있으면 계속 스크롤 (가만히 대고 있어도) → 화면 밖 줄까지 옮길 수 있다
  const autoScroll = (y) => {
    const sc = scrollParent();
    const r = sc ? sc.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const top = Math.max(r.top, 0); const bottom = Math.min(r.bottom, window.innerHeight);
    const dy = y < top + 70 ? -14 : y > bottom - 70 ? 14 : 0;
    if (!dy) return false;
    const before = sc ? sc.scrollTop : window.scrollY;
    if (sc) sc.scrollTop += dy; else window.scrollBy(0, dy);
    return (sc ? sc.scrollTop : window.scrollY) !== before;
  };
  const overAt = (x, y) => {
    const el = document.elementFromPoint ? document.elementFromPoint(x, y) : null;
    const row = el && el.closest ? el.closest('[data-work-row]') : null;
    return row ? row.getAttribute('data-work-row') : null;
  };
  const markOver = (d, oid) => { if (oid && oid !== d.overId) { d.overId = oid; setDrag({ id: d.id, overId: oid }); } };
  useEffect(() => () => { if (dragRef.current && dragRef.current.timer) clearInterval(dragRef.current.timer); }, []);
  const gripDown = (e, id) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (er) { /* 무시 */ }
    const d = { id, overId: null, x: e.clientX, y: e.clientY, timer: null };
    d.timer = setInterval(() => {
      if (dragRef.current !== d || d.y == null) return;
      if (autoScroll(d.y)) markOver(d, overAt(d.x, d.y));
    }, 40);
    dragRef.current = d;
    setDrag({ id, overId: null });
    setEditId(null);
  };
  const gripMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    e.preventDefault();
    d.x = e.clientX; d.y = e.clientY;
    markOver(d, overAt(e.clientX, e.clientY));
  };
  const gripUp = (e) => {
    const d = dragRef.current;
    if (d && d.timer) clearInterval(d.timer);
    dragRef.current = null;
    setDrag(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (er) { /* 무시 */ }
    if (d && d.overId && d.overId !== d.id) {
      const to = items.findIndex(i => i.id === d.overId);
      if (to >= 0) move(d.id, to);
    }
  };
  const grip = (id, big = false) => (
    <span data-work-grip={id} role="button" aria-label={Z('끌어서 순서 바꾸기', '拖动排序')} title={Z('끌어서 순서 바꾸기', '拖动排序')}
      onPointerDown={e => gripDown(e, id)} onPointerMove={gripMove} onPointerUp={gripUp} onPointerCancel={gripUp}
      style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', cursor: drag ? 'grabbing' : 'grab', color: drag && drag.id === id ? ORANGE : '#A1A1AA',
               fontSize: big ? 24 : 20, lineHeight: 1, padding: big ? '6px 4px' : '7px 2px', flexShrink: 0, alignSelf: big ? 'center' : 'flex-start' }}>⠿</span>
  );
  // 끌고 있는 줄이 놓일 자리 표시 (위로 옮기면 위쪽 선, 아래로 옮기면 아래쪽 선)
  const dropLine = (id) => {
    if (!drag || !drag.overId || drag.overId !== id || drag.id === id) return null;
    const from = items.findIndex(i => i.id === drag.id);
    const to = items.findIndex(i => i.id === id);
    return to < from ? 'top' : 'bottom';
  };

  // V157: 체크하면 그날 내역으로 — 목록에서 빠지고, 6초 동안 '되돌리기'
  const toggle = (id) => {
    if (!onCheck) return;
    if (isDone(id)) { onCheck(id, false); return; }
    onCheck(id, true);
    const it = items.find(i => i.id === id);
    const nm = String(it ? titleOf(it) : '').slice(0, 18);
    flash(Z(`✓ '${nm}' — 오늘 한 일로 저장했어요`, `✓「${nm}」已存到今天的记录`), () => onCheck(id, false));
  };
  const undoCheck = (id) => { if (onCheck) onCheck(id, false); };

  const setAmount = (it, r, raw) => {
    setDrafts(d => ({ ...d, [it.id]: raw }));
    const n = parseFloat(String(raw).replace(',', '.'));
    if (!isFinite(n) || n < 0) return;
    const sv = Number(r?.serving_g) || 0;
    const grams = it.mode === 'serv' && sv > 0 ? n * sv : n;
    patchItem(it.id, { grams: Math.round(grams * 10) / 10 });
  };

  // ── 레시피 고르기 목록 ────────────────────────────────────────────────────
  const countIn = useMemo(() => {
    const m = {};
    for (const i of items) if (i.kind === 'recipe') m[i.recipeId] = (m[i.recipeId] || 0) + 1;
    return m;
  }, [items]);
  const pickGroups = useMemo(() => {
    const kw = q.trim().toLowerCase().replace(/\s+/g, '');
    const out = [];
    for (const c of categories || []) {
      const list = (c?.items || []).filter(r => {
        if (!r || !r.id || !r.name) return false;
        if (typeFilter !== 'all' && r.type !== typeFilter) return false;
        if (!kw) return true;
        const hay = `${r.name}|${r.name_zh || ''}|${r.recipeCode || ''}`.toLowerCase().replace(/\s+/g, '');
        return hay.includes(kw);
      });
      if (list.length) out.push({ cat: c, list });
    }
    return out;
  }, [categories, q, typeFilter]);

  // ── 저장 / 내역 ───────────────────────────────────────────────────────────
  const snapshotItems = () => items.map(i => {
    const c = workChecks?.[i.id];
    const r = i.kind === 'recipe' ? recipesById.get(i.recipeId) : null;
    const base = { ...i };
    if (r) { base.name = r.name || i.name; if (r.name_zh) base.name_zh = r.name_zh; }
    return { ...base, done: !!c?.done, ...(c?.done && c.at ? { at: c.at } : {}), ...(c?.done && c.by ? { by: c.by } : {}) };
  });

  const workerName = String(user?.displayName || String(user?.email || '').split('@')[0] || '').slice(0, 30);

  // V156 날이 바뀌면 자동으로 넘긴다 (App에서 하면 autoRollover=false)
  useEffect(() => {
    if (!autoRollover || !onCommitWork) return;
    if (!needsRollover(workToday, today)) return;
    const r = archiveDone({ workToday, workChecks, workHistory, today, auto: true });
    if (r) onCommitWork({ workToday: r.workToday, workChecks: r.workChecks, workHistory: r.workHistory });
  }, [listDate, items.length, today]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── V156 회의 저장 → 테스크를 오늘의 작업에 ─────────────────────────────────
  // V157: 회의는 적는 대로 자동 저장 → 여기가 자주 불린다. 오늘의 작업이 안 바뀌면 그 칸은 안 보낸다
  const saveMeeting = (draft) => {
    if (!onCommitWork) return;
    const prev = (meetings || []).find(x => x && x.id === draft.id);
    const m = normMeeting({ ...draft, createdAt: prev?.createdAt || draft.createdAt, createdBy: prev?.createdBy || draft.createdBy }, { who: workerName });
    const r = applyMeetingToWork(m, { workToday: { date: listDate, items }, workChecks, workHistory, today });
    const changed = r.added || r.updated || r.removed;
    onCommitWork({ meetings: upsertMeeting(meetings, m), ...(changed ? { workToday: r.workToday } : {}) });
    const parts = [];
    if (r.added) parts.push(Z(`${r.added}개를 넣었어요`, `加了 ${r.added} 项`));
    if (r.removed) parts.push(Z(`${r.removed}개를 뺐어요`, `去掉了 ${r.removed} 项`));
    if (parts.length) flash(Z(`오늘의 작업에 테스크 ${parts.join(', ')}`, `今日工作：任务${parts.join('，')}`));
  };
  const deleteMeeting = (id) => {
    if (!onCommitWork) return;
    const r = removeMeetingFromWork(id, { workToday: { date: listDate, items }, workChecks });
    onCommitWork({ meetings: (meetings || []).filter(m => m && m.id !== id), ...(r.removed ? { workToday: r.workToday } : {}) });
    flash(r.removed ? Z(`회의를 지웠어요 · 오늘의 작업에서 테스크 ${r.removed}개를 뺐어요`, `已删除会议 · 从今日工作去掉 ${r.removed} 项`) : Z('회의를 지웠어요', '已删除会议'));
  };
  const openMeeting = (meetingId) => {
    if (!(meetings || []).some(m => m && m.id === meetingId)) { flash(Z('이 회의는 지워졌어요', '这个会议已删除')); return; }
    setTab('meeting');
    setOpenMeetingId(meetingId);
  };

  const loadFromHistory = (entry, replace) => {
    const fresh = (entry?.items || []).map(i => {
      const { done, at, by, pending, ...rest } = i;   // 체크는 새로 시작
      const r = rest.kind === 'recipe' ? recipesById.get(rest.recipeId) : null;
      return { ...rest, id: newWorkId(), ...(r ? { name: r.name, ...(r.name_zh ? { name_zh: r.name_zh } : {}) } : {}) };
    });
    const next = replace ? fresh : [...items, ...fresh];
    commit(next, today);
    setView('today');
    flash(Z(`${fresh.length}개를 오늘 목록에 넣었어요`, `已把 ${fresh.length} 项放进今日清单`));
  };

  // V154: 인쇄는 고르기 창을 거친다 (몇 개는 빼고, 재료 칸 넣을지)
  const print = (list, date, memo) => setPrintDlg({ items: list, date, memo });
  // V155: 위챗 — 걸러 보는 중이면 보이는 항목만 골라 둔다
  const share = (list, date, memo, off) => setShareDlg({ items: list, date, memo, off: off || [] });
  const todaySnap = () => active.map(i => ({ ...i, done: false }));   // V157 인쇄·위챗은 남은 작업만

  // ── V155 걸러 보기 · 담당자 이름 ───────────────────────────────────────────
  const owners = useMemo(() => ownerSuggestions({
    employees, items, workChecks, history: workHistory, meetings,
    me: String(user?.displayName || '').trim(),
  }), [employees, items, workChecks, workHistory, meetings, user]);
  const fromCount = active.filter(i => i.from).length;
  const ownersInList = [...new Set(active.map(i => i.owner).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const noOwnerCount = active.filter(i => !i.owner).length;
  const effSrc = srcFilter === 'meeting' && fromCount > 0 ? 'meeting' : 'all';
  const effOwner = ownerFilter === '-' ? (noOwnerCount && ownersInList.length ? '-' : '')
    : (ownerFilter && ownersInList.includes(ownerFilter) ? ownerFilter : '');
  const filtering = effSrc !== 'all' || !!effOwner;
  const passes = (i) => (effSrc === 'all' || !!i.from)
    && (!effOwner || (effOwner === '-' ? !i.owner : i.owner === effOwner));
  const visible = filtering ? active.filter(passes) : active;
  const showFilters = active.length > 1 && ((fromCount > 0 && fromCount < active.length) || ownersInList.length > 0);

  // V157 내역 화면 — 저장된 내역 + 체크해 둔 작업(체크한 날짜별). 날이 바뀌면 정식으로 옮겨진다
  const mergedHistory = useMemo(() => {
    if (!doneItems.length) return workHistory || [];
    const hist = [...(workHistory || [])];
    const byDate = new Map();
    for (const it of doneItems) {
      const c = workChecks[it.id] || {};
      const d = c.at ? todayKey(new Date(c.at)) : (listDate || today);
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push({ ...it, done: true, at: c.at, by: c.by, pending: true });
    }
    for (const [date, list] of byDate) {
      const id = `day-${date}`;
      const k = hist.findIndex(h => h && h.id === id);
      const ids = new Set(list.map(x => x.id));
      if (k >= 0) hist[k] = { ...hist[k], items: [...(hist[k].items || []).filter(x => !ids.has(x.id)), ...list] };
      else hist.push({ id, date, items: list, daily: true, savedBy: '' });
    }
    hist.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0));
    return hist;
  }, [workHistory, doneItems, workChecks, listDate, today]);
  const doPrint = (list, withIngredients) => {
    const ok = printWorkList({ items: list, recipesById, zhMap, date: printDlg?.date, memo: printDlg?.memo, storeName, withIngredients });
    if (!ok) flash(Z('인쇄 창을 열 수 없어요 (팝업 차단을 확인해 주세요)', '打不开打印窗口（请允许弹出窗口）'));
    else setPrintDlg(null);
  };

  // ── 공통 스타일 ───────────────────────────────────────────────────────────
  const card = { background: '#fff', borderRadius: 14, padding: isMobile ? '12px 12px' : '14px 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' };
  const btn = (bg, color, border) => ({
    padding: '8px 12px', borderRadius: 9, border: `1.5px solid ${border || bg}`, background: bg, color,
    fontSize: 13, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
  });
  const chip = (on) => ({
    padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    border: `1.5px solid ${on ? ORANGE : '#E4E4E7'}`, background: on ? '#FFF4EC' : '#fff', color: on ? '#C2410C' : '#52525B',
  });
  const typeLabel = (t) => (t === 'semi' ? Z('반제품', '半成品') : t === 'menu' ? Z('메뉴', '菜品') : '');

  // ── 레시피 고르기 + 직접 입력 ─────────────────────────────────────────────
  const picker = (
    <div data-work-picker style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, height: '100%' }}>
      <div style={{ ...card, padding: 12, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#18181B', marginBottom: 6 }}>✏️ {Z('직접 입력', '直接输入')}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input data-work-text value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent?.isComposing && e.keyCode !== 229) { /* 병음 입력 중 Enter는 글자 확정 */ e.preventDefault(); addText(); } }}
            placeholder={Z('예: 냉장고 정리, 양파 손질', '例：整理冰箱、洋葱处理')}
            style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 9, border: '1.5px solid #E4E4E7', fontSize: 14 }} />
          <button type="button" data-work-text-add onClick={addText} disabled={!text.trim()}
            style={{ ...btn(text.trim() ? ORANGE : '#E4E4E7', '#fff'), cursor: text.trim() ? 'pointer' : 'default' }}>
            {Z('추가', '添加')}
          </button>
        </div>
        <button type="button" data-work-add-more onClick={() => setAddMore(v => !v)}
          style={{ border: 'none', background: 'none', padding: '6px 0 0', fontSize: 12, fontWeight: 800, color: '#C2410C', cursor: 'pointer' }}>
          {addMore ? '▾' : '▸'} {Z('담당자 · 마감일 · 내용', '负责人 · 截止 · 内容')}
          {!addMore && (addMeta.owner || addMeta.due) ? <span style={{ fontWeight: 600, color: '#71717A' }}> · {[addMeta.owner, addMeta.due && fmtDue({ due: addMeta.due }, lang, today)].filter(Boolean).join(' · ')}</span> : null}
        </button>
        {addMore && (
          <MetaFields Z={Z} value={addMeta} onChange={setAddMeta} owners={owners} today={today} compact />
        )}
      </div>

      <div style={{ ...card, padding: 12, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#18181B', marginBottom: 6 }}>
          📋 {Z('레시피에서 고르기', '从配方里选')} <span style={{ fontWeight: 500, color: '#71717A' }}>· {Z('누르면 오늘 목록에 들어가요', '点一下就加到今日清单')}</span>
        </div>
        <input data-work-search value={q} onChange={e => setQ(e.target.value)}
          placeholder={Z('🔍 레시피 찾기', '🔍 搜索配方')}
          style={{ padding: '8px 10px', borderRadius: 9, border: '1.5px solid #E4E4E7', fontSize: 14, marginBottom: 6 }} />
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {[['all', Z('전체', '全部')], ['semi', Z('반제품', '半成品')], ['menu', Z('메뉴', '菜品')]].map(([k, l]) => (
            <button key={k} type="button" data-work-type={k} onClick={() => setTypeFilter(k)} style={chip(typeFilter === k)}>{l}</button>
          ))}
        </div>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 120 }}>
          {pickGroups.length === 0 && (
            <div style={{ fontSize: 13, color: '#9ca3af', padding: '16px 4px' }}>
              {recipes.length ? Z('찾는 레시피가 없어요.', '没有找到配方。') : Z('아직 레시피가 없어요. 레시피 탭에서 먼저 만들어 주세요.', '还没有配方，请先在「配方」里添加。')}
            </div>
          )}
          {pickGroups.map(({ cat, list }) => (
            <div key={cat.name || cat.id} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#71717A', padding: '4px 2px' }}>
                {zh ? (cat.name_zh || cat.name) : cat.name}
              </div>
              {list.map(r => {
                const n = countIn[r.id] || 0;
                const base = recipeBase(r);
                return (
                  <button key={r.id} type="button" data-work-pick={r.id} onClick={() => addRecipe(r)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 8px',
                             marginBottom: 4, borderRadius: 10, border: `1.5px solid ${n ? '#FDBA74' : '#F4F4F5'}`,
                             background: n ? '#FFF7ED' : '#FAFAFA', cursor: 'pointer' }}>
                    <span style={{ fontSize: 18, color: ORANGE, fontWeight: 900, width: 18, textAlign: 'center' }}>+</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: '#18181B' }}>
                      <BiName ko={r.name} zh={r.name_zh} size={11} pron={false} />
                    </span>
                    <span style={{ fontSize: 11, color: '#71717A', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'block', fontWeight: 700, color: r.type === 'semi' ? '#1e40af' : '#52525B' }}>{typeLabel(r.type)}</span>
                      {base > 0 && fmtAmount(base, 'g', lang)}
                    </span>
                    {n > 0 && <span data-work-pick-count style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: ORANGE, borderRadius: 999, padding: '1px 7px' }}>✓{n > 1 ? ` ${n}` : ''}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── V156 회의에서 온 테스크 배지 · 담당 · 마감 ─────────────────────────────────
  const meetBadge = (it) => it.from ? (
    <button type="button" data-work-from={it.from.meetingId} onClick={(e) => { e.stopPropagation(); openMeeting(it.from.meetingId); }}
      title={Z('이 회의 열기', '打开这个会议')}
      style={{ border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 800, color: MEET.color, background: MEET.bg, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      🗓 {it.from.title || Z('회의', '会议')}
    </button>
  ) : null;
  const metaLine = (it, done) => {
    const due = fmtDue(it, lang, today);
    if (!it.owner && !due) return null;
    const st = dueState(it, { done, today, listDate: listDate || today });
    const dueColor = st === 'over' ? '#B91C1C' : st === 'today' ? '#C2410C' : '#52525B';
    const dueBg = st === 'over' ? '#FEE2E2' : st === 'today' ? '#FFF4EC' : '#F4F4F5';
    return (
      <div data-work-meta style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
        {it.owner && (
          <button type="button" data-work-owner={it.owner} onClick={() => setOwnerFilter(effOwner === it.owner ? '' : it.owner)}
            title={Z('이 사람 일만 보기', '只看这个人的工作')}
            style={{ border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800, color: '#1A56A0', background: '#EFF6FF', borderRadius: 6, padding: '2px 8px' }}>
            👤 {it.owner}
          </button>
        )}
        {due && (
          <span data-work-due={st || 'ok'} style={{ fontSize: 12.5, fontWeight: 800, color: dueColor, background: dueBg, borderRadius: 6, padding: '2px 8px' }}>
            ⏰ {due}{Z('까지', '前')}{st === 'over' ? ` · ${Z('지났어요', '已过期')}` : ''}
          </span>
        )}
      </div>
    );
  };
  const titleOf = (it) => {
    if (it.kind === 'text') return it.text;
    const r = recipesById.get(it.recipeId);
    const ko = r?.name || it.name; const z = r?.name_zh || it.name_zh;
    return zh ? (z || ko) : ko;
  };

  // ── V155 ↕ 순서 바꾸기 — 한 줄로 줄여서 ▲▼ · 맨 위로 · (PC) 끌어서 놓기 ─────────────
  const moveBtn = (on) => ({
    width: 38, height: 34, borderRadius: 9, border: '1.5px solid #E4E4E7', background: on ? '#fff' : '#FAFAFA',
    color: on ? '#18181B' : '#D4D4D8', fontSize: 15, fontWeight: 900, cursor: on ? 'pointer' : 'default', flexShrink: 0,
  });
  const reorderRow = (it, idx) => {   // idx = 남은 작업(active) 안의 자리
    const done = isDone(it.id);
    const first = idx === 0; const last = idx === active.length - 1;
    const at = (k) => items.indexOf(active[k]);   // 남은 작업 k번째의 전체 목록 자리
    const line = dropLine(it.id);
    return (
      <div key={it.id} data-work-reorder-item={it.id} data-work-row={it.id}
        style={{ ...card, padding: '8px 10px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8,
                 opacity: drag && drag.id === it.id ? 0.45 : 1,
                 boxShadow: line ? `0 ${line === 'top' ? -3 : 3}px 0 ${ORANGE}, 0 1px 6px rgba(0,0,0,0.05)` : card.boxShadow }}>
        {grip(it.id, true)}
        <span style={{ width: 22, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#A1A1AA' }}>{idx + 1}</span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: done ? '#71717A' : '#18181B', textDecoration: done ? 'line-through' : 'none',
                         overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(it)}</span>
          {it.from && !isMobile && <span style={{ fontSize: 11.5, fontWeight: 700, color: MEET.color, whiteSpace: 'nowrap' }}>🗓</span>}
          {it.owner && !isMobile && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1A56A0', whiteSpace: 'nowrap' }}>👤 {it.owner}</span>}
        </span>
        <button type="button" data-work-move-top={it.id} disabled={first} onClick={() => move(it.id, at(0))} title={Z('맨 위로', '移到最上面')} style={moveBtn(!first)}>⤒</button>
        <button type="button" data-work-move-up={it.id} disabled={first} onClick={() => move(it.id, at(idx - 1))} title={Z('위로', '上移')} style={moveBtn(!first)}>▲</button>
        <button type="button" data-work-move-down={it.id} disabled={last} onClick={() => move(it.id, at(idx + 1))} title={Z('아래로', '下移')} style={moveBtn(!last)}>▼</button>
      </div>
    );
  };

  // ── 오늘 목록의 한 줄 ─────────────────────────────────────────────────────
  const row = (it, idx) => {
    const done = isDone(it.id);
    const chk = workChecks?.[it.id];
    const r = it.kind === 'recipe' ? recipesById.get(it.recipeId) : null;
    const sv = Number(r?.serving_g) || 0;
    const base = r ? recipeBase(r) : 0;
    const grams = Number(it.grams) || 0;
    const mode = it.mode === 'serv' && sv > 0 ? 'serv' : 'g';
    const shownVal = drafts[it.id] != null ? drafts[it.id] : fmtNum(mode === 'serv' ? grams / sv : grams);
    const ings = r ? scaleIngredients(r, grams) : [];
    // PC는 펼쳐서 보여준다 (다 한 작업·재료가 아주 많은 작업은 접어 둠). 모바일은 접어서
    const open = openIngs[it.id] != null ? openIngs[it.id] : !isMobile && !done && ings.length <= 12;
    const line = dropLine(it.id);
    return (
      <div key={it.id} data-work-item={it.id} data-work-row={it.id} data-done={done ? '1' : '0'}
        style={{ ...card, padding: isMobile ? '10px 8px 10px 4px' : '12px 14px 12px 8px', marginBottom: 8,
                 border: `1.5px solid ${done ? '#BBF7D0' : '#fff'}`, background: done ? '#F7FEF9' : '#fff',
                 opacity: drag && drag.id === it.id ? 0.45 : 1,
                 boxShadow: line ? `0 ${line === 'top' ? -3 : 3}px 0 ${ORANGE}, 0 1px 6px rgba(0,0,0,0.05)` : card.boxShadow }}>
        <div style={{ display: 'flex', gap: isMobile ? 6 : 8, alignItems: 'flex-start' }}>
          {grip(it.id)}
          <button type="button" data-work-check={it.id} onClick={() => toggle(it.id)}
            aria-label={done ? Z('완료 취소', '取消完成') : Z('완료', '完成')}
            style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 9, cursor: 'pointer',
                     border: `2.5px solid ${done ? '#16A34A' : '#A1A1AA'}`, background: done ? '#16A34A' : '#fff',
                     color: '#fff', fontSize: 20, fontWeight: 900, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {done ? '✓' : ''}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div onClick={() => toggle(it.id)} style={{ cursor: 'pointer', display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#A1A1AA', fontWeight: 700 }}>{idx + 1}</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: done ? '#71717A' : '#18181B', textDecoration: done ? 'line-through' : 'none' }}>
                {it.kind === 'text'
                  ? it.text
                  : <BiName ko={r?.name || it.name} zh={r?.name_zh || it.name_zh} size={12} pron={false} />}
              </span>
              {r && <span style={{ fontSize: 11, fontWeight: 700, color: r.type === 'semi' ? '#1e40af' : '#52525B',
                                   background: r.type === 'semi' ? '#dbeafe' : '#F4F4F5', borderRadius: 6, padding: '1px 6px' }}>{typeLabel(r.type)}</span>}
              {meetBadge(it)}
            </div>
            {it.kind === 'recipe' && !r && (
              <div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>⚠ {Z('이 레시피를 찾을 수 없어요 (지워졌거나 이름이 바뀌었어요)', '找不到这个配方（可能已删除）')}</div>
            )}
            {done && chk && (
              <div data-work-done-by style={{ fontSize: 12, color: '#16A34A', fontWeight: 700, marginTop: 2 }}>
                ✓ {hhmm(chk.at)}{chk.by ? ` · ${chk.by}` : ''}
              </div>
            )}
            {metaLine(it, done)}
            {it.note && editId !== it.id && (
              <div data-work-note style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, color: done ? '#71717A' : '#3F3F46', marginTop: 6,
                                           background: '#FAFAFA', border: '1px solid #F4F4F5', borderRadius: 8, padding: '6px 9px', lineHeight: 1.5 }}>
                {it.note}
              </div>
            )}
            {editId === it.id && (
              <ItemEditor Z={Z} btn={btn} it={it} owners={owners} today={today}
                onCancel={() => setEditId(null)} onSave={(v) => saveEdit(it.id, v)} />
            )}

            {/* 만들 양 */}
            {it.kind === 'recipe' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: '#71717A', fontWeight: 700 }}>{Z('만들 양', '要做的量')}</span>
                <input data-work-grams={it.id} type="text" inputMode="decimal" value={shownVal}
                  onChange={e => setAmount(it, r, e.target.value)}
                  onBlur={() => setDrafts(d => { const n = { ...d }; delete n[it.id]; return n; })}
                  style={{ width: 76, padding: '6px 8px', borderRadius: 8, border: '1.5px solid #D4D4D8', fontSize: 15, fontWeight: 800, textAlign: 'right' }} />
                {sv > 0 ? (
                  <button type="button" data-work-mode={it.id}
                    onClick={() => { setDrafts(d => { const n = { ...d }; delete n[it.id]; return n; }); patchItem(it.id, { mode: mode === 'serv' ? 'g' : 'serv' }); }}
                    title={Z('g ↔ 인분 바꾸기', '切换 g ↔ 份')}
                    style={{ ...chip(true), padding: '5px 10px' }}>
                    {mode === 'serv' ? Z('인분', '份') : 'g'} ⇄
                  </button>
                ) : <span style={{ fontSize: 14, fontWeight: 700 }}>g</span>}
                <span data-work-amount-note style={{ fontSize: 12, color: '#52525B' }}>
                  {mode === 'serv'
                    ? `= ${fmtAmount(grams, 'g', lang)}`
                    : sv > 0 ? `≈ ${fmtNum(grams / sv)}${Z('인분', '份')}` : ''}
                </span>
                {base > 0 && (
                  <span style={{ display: 'inline-flex', gap: 4, marginLeft: isMobile ? 0 : 'auto' }}>
                    {MULTS.map(k => {
                      const on = Math.abs(grams - base * k) < 0.05;
                      return (
                        <button key={k} type="button" data-work-mult={`${it.id}:${k}`}
                          title={Z(`기준량 ${fmtAmount(base, 'g')}의 ${k}배`, `配方量 ${fmtAmount(base, 'g')} 的 ${k} 倍`)}
                          onClick={() => { setDrafts(d => { const n = { ...d }; delete n[it.id]; return n; }); patchItem(it.id, { grams: Math.round(base * k * 10) / 10 }); }}
                          style={{ ...chip(on), padding: '4px 8px' }}>{MULT_LABEL[k]}</button>
                      );
                    })}
                  </span>
                )}
              </div>
            )}

            {/* 그 양에 맞춘 재료 */}
            {ings.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button type="button" data-work-ings={it.id} onClick={() => setOpenIngs(o => ({ ...o, [it.id]: !open }))}
                  style={{ border: 'none', background: 'none', padding: 0, fontSize: 12, fontWeight: 800, color: '#C2410C', cursor: 'pointer' }}>
                  {open ? '▾' : '▸'} {Z(`재료 ${ings.length}가지`, `用料 ${ings.length} 种`)}
                </button>
                {open && (
                  <div data-work-ing-list style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '2px 16px', marginTop: 4 }}>
                    {ings.map((ing, k) => {
                      const semi = semiByName.get(ing.name.replace(/\s+/g, ''));
                      return (
                        <div key={k} data-work-ing={ing.name}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '1px dashed #F4F4F5', fontSize: 13 }}>
                          <span style={{ flex: 1, minWidth: 0, color: '#27272A', fontWeight: 600 }}>
                            <BiName ko={ing.name} zhMap={zhMap} size={11} pron={false} />
                          </span>
                          {semi && semi.id !== it.recipeId && (
                            <button type="button" data-work-add-semi={semi.id} onClick={() => addRecipe(semi, ing.qty)}
                              title={Z('이 반제품 만들기도 오늘 작업에 넣기', '把做这个半成品也加到今日工作')}
                              style={{ ...chip(false), padding: '2px 7px', fontSize: 11 }}>+ {Z('작업', '工作')}</button>
                          )}
                          <span style={{ fontWeight: 800, color: '#18181B', whiteSpace: 'nowrap' }}>{fmtAmount(ing.qty, ing.unit, lang)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <button type="button" data-work-remove={it.id} onClick={() => removeItem(it.id)} title={Z('빼기', '删除')}
              style={{ border: 'none', background: 'none', color: '#A1A1AA', fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
            <button type="button" data-work-edit={it.id} onClick={() => setEditId(editId === it.id ? null : it.id)}
              title={Z('내용 · 기한 · 담당자 고치기', '修改内容 · 截止 · 负责人')}
              style={{ border: 'none', background: editId === it.id ? '#FFF4EC' : 'none', borderRadius: 7, color: editId === it.id ? '#C2410C' : '#71717A', fontSize: 16, cursor: 'pointer', padding: '3px 5px' }}>✎</button>
          </div>
        </div>
      </div>
    );
  };

  // ── 남은 작업에 필요한 재료 합계 ───────────────────────────────────────────
  const totalIngs = useMemo(() => sumIngredients(items
    .filter(i => i.kind === 'recipe' && !isDone(i.id))
    .map(i => ({ recipe: recipesById.get(i.recipeId), grams: i.grams })))
    .sort((a, b) => a.name.localeCompare(b.name)),
  // eslint-disable-next-line
  [items, workChecks, recipesById]);

  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  // ── 오늘 목록 화면 ────────────────────────────────────────────────────────
  const todayPane = (
    <div style={{ minWidth: 0 }}>
      {/* 머리 */}
      <div style={{ ...card, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#18181B' }}>📋 {Z('오늘의 작업', '今日工作')}</div>
            <div style={{ fontSize: 13, color: '#71717A', marginTop: 2 }}>{fmtDay(listDate && items.length ? listDate : today, lang)}</div>
          </div>
          <button type="button" data-work-reorder disabled={active.length < 2 && !reorder}
            onClick={() => { setReorder(v => !v); setEditId(null); setDrag(null); if (dragRef.current && dragRef.current.timer) clearInterval(dragRef.current.timer); dragRef.current = null; }}
            style={{ ...btn(reorder ? '#18181B' : '#fff', reorder ? '#fff' : (active.length > 1 ? '#18181B' : '#A1A1AA'), reorder ? '#18181B' : '#D4D4D8'),
                     cursor: active.length > 1 || reorder ? 'pointer' : 'default' }}>
            {reorder ? `✓ ${Z('순서 다 했어요', '排好了')}` : `↕ ${Z('순서', '排序')}`}
          </button>
          <button type="button" data-work-share disabled={!active.length}
            onClick={() => share(todaySnap(), today, '', filtering ? active.filter(i => !passes(i)).map(i => i.id) : [])}
            style={{ ...btn(active.length ? GREEN : '#E4E4E7', '#fff'), cursor: active.length ? 'pointer' : 'default' }}>💬 {Z('위챗', '微信')}</button>
          <button type="button" data-work-print disabled={!active.length}
            onClick={() => print(todaySnap(), today, '')}
            style={{ ...btn('#fff', active.length ? '#18181B' : '#A1A1AA', '#D4D4D8'), cursor: active.length ? 'pointer' : 'default' }}>🖨️ {Z('인쇄', '打印')}</button>
          <button type="button" data-work-history-open onClick={() => setView('history')} style={btn('#fff', '#18181B', '#D4D4D8')}>
            📅 {Z('내역', '记录')}{mergedHistory.length ? ` ${mergedHistory.length}` : ''}
          </button>
        </div>
        {items.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div data-work-progress style={{ fontSize: 13, fontWeight: 800, color: active.length === 0 ? '#16A34A' : '#18181B', marginBottom: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>{active.length === 0 ? `🎉 ${Z('남은 작업 없음', '没有剩下的工作')}` : Z(`남은 작업 ${active.length}`, `剩下 ${active.length} 项`)}</span>
              {doneCount > 0 && <span style={{ color: '#16A34A' }}>✓ {Z(`다 한 작업 ${doneCount}`, `已完成 ${doneCount}`)}</span>}
            </div>
            <div style={{ height: 8, background: '#F4F4F5', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: active.length === 0 ? '#16A34A' : ORANGE, transition: 'width .2s' }} />
            </div>
          </div>
        )}
        {onCommitWork && (
          <div data-work-carry-note style={{ fontSize: 12, color: '#71717A', marginTop: 8 }}>
            {Z('체크하면 그날 내역에 저장되고 목록에서 빠져요. 안 한 작업은 다음 날에도 남아요.', '打勾后会存到当天的记录，并从清单里消失。没做完的第二天还会留着。')}
          </div>
        )}
      </div>

      {/* 어제 목록이 그대로 남아 있을 때 */}
      {stale && (
        <div data-work-stale style={{ ...card, marginBottom: 10, background: '#FFFBEB', border: '1.5px solid #FCD34D' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#92400E' }}>
            📅 {Z(`이 목록은 ${fmtDay(listDate, 'ko')} 목록이에요.`, `这是 ${fmtDay(listDate, 'zh')} 的清单。`)}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" data-work-stale-continue onClick={() => commit(items, today)} style={btn('#fff', '#92400E', '#FCD34D')}>
              {Z('오늘 목록으로 계속 쓰기', '继续当今天的清单用')}
            </button>
          </div>
        </div>
      )}

      {isMobile && (
        <button type="button" data-work-add-open onClick={() => setPickerOpen(true)}
          style={{ ...btn(ORANGE, '#fff'), width: '100%', padding: '12px', fontSize: 15, marginBottom: 10 }}>
          ＋ {Z('작업 추가 (레시피 · 직접 입력)', '添加工作（配方 · 直接输入）')}
        </button>
      )}

      {/* V155 순서 바꾸기 안내 */}
      {reorder && active.length > 0 && (
        <div data-work-reorder-help style={{ ...card, marginBottom: 8, background: '#18181B', color: '#fff', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, lineHeight: 1.5 }}>
            ↕ {Z('⠿를 잡고 끌거나 ▲▼로 옮겨요. ⤒는 맨 위로.', '按住 ⠿ 拖动，或用 ▲▼ 移动，⤒ 移到最上面。')}
          </span>
          <button type="button" data-work-reorder-done onClick={() => setReorder(false)} style={btn(ORANGE, '#fff')}>{Z('다 했어요', '好了')}</button>
        </div>
      )}

      {/* V155 담당자별 · V156 회의 테스크만 걸러 보기 */}
      {!reorder && showFilters && (
        <div data-work-filters style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          {fromCount > 0 && fromCount < items.length && (
            <>
              <button type="button" data-work-filter-src="all" onClick={() => setSrcFilter('all')} style={chip(effSrc === 'all')}>{Z('전체', '全部')} {active.length}</button>
              <button type="button" data-work-filter-src="meeting" onClick={() => setSrcFilter('meeting')} style={chip(effSrc === 'meeting')}>🗓 {Z('회의 테스크', '会议任务')} {fromCount}</button>
              {ownersInList.length > 0 && <span style={{ width: 1, height: 18, background: '#E4E4E7', margin: '0 2px' }} />}
            </>
          )}
          {ownersInList.length > 0 && (
            <>
              <button type="button" data-work-filter-owner="" onClick={() => setOwnerFilter('')} style={chip(!effOwner)}>👤 {Z('모두', '所有人')}</button>
              {ownersInList.map(o => (
                <button key={o} type="button" data-work-filter-owner={o} onClick={() => setOwnerFilter(effOwner === o ? '' : o)} style={chip(effOwner === o)}>
                  {o} {active.filter(i => i.owner === o).length}
                </button>
              ))}
              {noOwnerCount > 0 && (
                <button type="button" data-work-filter-owner="-" onClick={() => setOwnerFilter(effOwner === '-' ? '' : '-')} style={chip(effOwner === '-')}>
                  {Z('담당 없음', '没指定')} {noOwnerCount}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {active.length === 0 && doneCount > 0 ? (
        <div data-work-all-done style={{ ...card, textAlign: 'center', padding: '28px 16px', color: '#16A34A' }}>
          <div style={{ fontSize: 30 }}>🎉</div>
          <div style={{ fontSize: 15, fontWeight: 900, marginTop: 4 }}>{Z('오늘 작업을 다 했어요!', '今天的工作都做完了！')}</div>
          <div style={{ fontSize: 12.5, color: '#71717A', marginTop: 4 }}>{Z('다 한 작업은 📅 내역에 저장됐어요.', '做完的工作已存到「📅 记录」。')}</div>
        </div>
      ) : active.length === 0 ? (
        <div data-work-empty style={{ ...card, textAlign: 'center', padding: '36px 16px', color: '#71717A' }}>
          <div style={{ fontSize: 34 }}>🧑‍🍳</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#18181B', marginTop: 6 }}>{Z('오늘 할 작업을 추가해 보세요', '添加今天要做的工作')}</div>
          <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.6 }}>
            {isMobile
              ? Z('위의 ＋ 버튼을 눌러 레시피를 고르거나 직접 적어요.', '点上面的 ＋ 按钮，选配方或直接写。')
              : Z('왼쪽에서 레시피를 누르거나 직접 적어요.', '在左边点配方，或直接写。')}
            <br />{Z('📅 내역에서 예전 목록을 불러올 수도 있어요.', '也可以在「📅 记录」里调出以前的清单。')}
          </div>
        </div>
      ) : reorder ? active.map(reorderRow) : (
        <>
          {visible.map(it => row(it, active.indexOf(it)))}
          {filtering && visible.length === 0 && (
            <div data-work-filter-empty style={{ ...card, textAlign: 'center', color: '#71717A', fontSize: 13, padding: '20px 12px' }}>
              {Z('이 조건에 맞는 항목이 없어요.', '没有符合条件的项目。')}{' '}
              <button type="button" onClick={() => { setSrcFilter('all'); setOwnerFilter(''); }} style={{ ...chip(false) }}>{Z('모두 보기', '全部显示')}</button>
            </div>
          )}
          {filtering && visible.length > 0 && (
            <div style={{ fontSize: 12, color: '#71717A', textAlign: 'center', margin: '2px 0 8px' }}>
              {Z(`${active.length}개 중 ${visible.length}개만 보는 중`, `共 ${active.length} 项，现在只看 ${visible.length} 项`)} ·{' '}
              <button type="button" data-work-filter-clear onClick={() => { setSrcFilter('all'); setOwnerFilter(''); }}
                style={{ border: 'none', background: 'none', color: '#C2410C', fontWeight: 800, cursor: 'pointer', fontSize: 12, padding: 0 }}>{Z('모두 보기', '全部显示')}</button>
            </div>
          )}
        </>
      )}

      {/* V157 다 한 작업 — 목록에서는 빠지고 여기와 내역에. 잘못 눌렀으면 되돌리기 */}
      {doneItems.length > 0 && !reorder && (
        <div data-work-done-box style={{ ...card, marginBottom: 8, background: '#F7FEF9', border: '1.5px solid #DCFCE7' }}>
          <button type="button" data-work-done-toggle onClick={() => setShowDone(v => !v)}
            style={{ border: 'none', background: 'none', padding: 0, fontSize: 14, fontWeight: 800, color: '#15803D', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
            {showDone ? '▾' : '▸'} ✓ {Z(`다 한 작업 ${doneItems.length}개 · 내역에 저장됨`, `已完成 ${doneItems.length} 项 · 已存到记录`)}
          </button>
          {showDone && (
            <div style={{ marginTop: 8 }}>
              {doneItems.map(it => {
                const c = workChecks[it.id] || {};
                return (
                  <div key={it.id} data-work-done-item={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px', borderBottom: '1px dashed #DCFCE7', fontSize: 13.5 }}>
                    <span style={{ color: '#16A34A', fontWeight: 900 }}>✓</span>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: '#3F3F46', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(it)}</span>
                    <span style={{ fontSize: 12, color: '#16A34A', whiteSpace: 'nowrap' }}>{hhmm(c.at)}{c.by ? ` · ${c.by}` : ''}</span>
                    <button type="button" data-work-undo={it.id} onClick={() => undoCheck(it.id)}
                      style={{ border: '1.5px solid #D4D4D8', background: '#fff', borderRadius: 8, padding: '3px 8px', fontSize: 12, fontWeight: 800, cursor: 'pointer', color: '#52525B' }}>↩ {Z('되돌리기', '撤回')}</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {totalIngs.length > 0 && (
        <div style={{ ...card, marginTop: 4 }}>
          <button type="button" data-work-total-toggle onClick={() => setShowTotal(s => !s)}
            style={{ border: 'none', background: 'none', padding: 0, fontSize: 14, fontWeight: 800, color: '#18181B', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
            {showTotal ? '▾' : '▸'} 🧺 {Z(`남은 작업에 필요한 재료 모아보기 (${totalIngs.length}가지)`, `剩下的工作一共需要的食材（${totalIngs.length} 种）`)}
          </button>
          {showTotal && (
            <div data-work-total style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '2px 16px', marginTop: 8 }}>
              {totalIngs.map(ing => (
                <div key={`${ing.name}|${ing.unit}`} data-work-total-ing={ing.name}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '1px dashed #F4F4F5', fontSize: 13 }}>
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}><BiName ko={ing.name} zhMap={zhMap} size={11} pron={false} /></span>
                  <span style={{ fontWeight: 800 }}>{fmtAmount(ing.qty, ing.unit, lang)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div data-work-root ref={rootRef} style={{ background: '#f0f2f5', minHeight: '100%' }}>
      <div style={{ maxWidth: tab === 'meeting' ? 1400 : 1180, margin: '0 auto', padding: isMobile ? '12px 10px 80px' : '18px 16px 40px' }}>
        {/* V156 오늘의 작업 | 회의 */}
        <div data-work-tabs style={{ display: 'flex', gap: 6, marginBottom: 12, background: '#fff', borderRadius: 14, padding: 5, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
          {[['today', '📋', Z('오늘의 작업', '今日工作'), active.length], ['meeting', '🗓', Z('회의', '会议'), (meetings || []).length]].map(([k, ic, l, n]) => (
            <button key={k} type="button" data-work-tab={k} onClick={() => { setTab(k); if (k === 'today') setView('today'); }}
              style={{ flex: 1, padding: isMobile ? '10px 6px' : '11px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                       background: tab === k ? '#18181B' : 'transparent', color: tab === k ? '#fff' : '#52525B', fontSize: isMobile ? 15 : 16, fontWeight: 900 }}>
              {ic} {l}{n ? <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 800, opacity: 0.75 }}>{n}</span> : null}
            </button>
          ))}
        </div>
        {tab === 'meeting' ? (
          <MeetingPane Z={Z} lang={lang} isMobile={isMobile} card={card} btn={btn} today={today} storeName={storeName}
            meetings={meetings} workToday={workToday} workChecks={workChecks} workHistory={workHistory} owners={owners}
            openId={openMeetingId} onOpened={() => setOpenMeetingId(null)} onSave={saveMeeting} onDelete={deleteMeeting} />
        ) : view === 'history' ? (
          <HistoryPane history={mergedHistory} lang={lang} Z={Z} card={card} btn={btn} isMobile={isMobile}
            recipesById={recipesById} hasToday={active.length > 0} onUndo={undoCheck}
            onBack={() => setView('today')} onLoad={loadFromHistory}
            onDelete={(id) => { onDeleteHistory && onDeleteHistory(id); flash(Z('지웠어요', '已删除')); }}
            onPrint={(e) => print(e.items || [], e.date, e.memo)}
            onShare={(e) => share(e.items || [], e.date, e.memo)} />
        ) : isMobile ? todayPane : (
          <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 14, alignItems: 'start' }}>
            <div style={{ position: 'sticky', top: 0, height: 'calc(100vh - 54px - 36px)' }}>{picker}</div>
            {todayPane}
          </div>
        )}
      </div>

      {/* 모바일 '+ 작업 추가' 창 */}
      {isMobile && pickerOpen && view === 'today' && tab === 'today' && (
        <div data-work-picker-sheet style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginTop: 'auto', background: '#f0f2f5', borderRadius: '16px 16px 0 0', height: '86vh', display: 'flex', flexDirection: 'column', padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ flex: 1, fontSize: 16, fontWeight: 900 }}>＋ {Z('작업 추가', '添加工作')}
                <span style={{ fontSize: 12, color: '#71717A', fontWeight: 600 }}> · {Z(`지금 ${items.length}개`, `现在 ${items.length} 项`)}</span></div>
              <button type="button" data-work-add-close onClick={() => setPickerOpen(false)} style={btn(ORANGE, '#fff')}>{Z('다 했어요', '好了')}</button>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>{picker}</div>
          </div>
        </div>
      )}

      {printDlg && (
        <PrintDialog Z={Z} lang={lang} btn={btn} items={printDlg.items} recipesById={recipesById}
          onCancel={() => setPrintDlg(null)} onPrint={doPrint} />
      )}

      {shareDlg && (
        <ShareDialog Z={Z} lang={lang} btn={btn} chip={chip} isMobile={isMobile}
          items={shareDlg.items} initialOff={shareDlg.off} date={shareDlg.date} memo={shareDlg.memo} today={today}
          recipesById={recipesById} zhMap={zhMap} storeName={storeName} onClose={() => setShareDlg(null)} />
      )}


      {toast && (
        <div data-work-toast style={{ position: 'fixed', bottom: isMobile ? 76 : 24, zIndex: 500,
                                      ...(isMobile ? { left: 12, right: 12 } : { left: '50%', transform: 'translateX(-50%)', maxWidth: 560 }),
                                      background: '#18181B', color: '#fff', padding: toast.undo ? '8px 8px 8px 14px' : '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                                      boxShadow: '0 4px 14px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>{toast.msg}</span>
          {toast.undo && (
            <button type="button" data-work-toast-undo onClick={() => { const u = toast.undo; setToast(null); clearTimeout(toastTimer.current); u(); }}
              style={{ border: 'none', background: ORANGE, color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}>↩ {Z('되돌리기', '撤回')}</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── V154 인쇄 고르기 — 뺄 작업 체크 해제, 재료 칸 넣을지 ─────────────────────────
function PrintDialog({ Z, lang, btn, items = [], recipesById, onCancel, onPrint }) {
  const [off, setOff] = useState(() => new Set());
  const [withIngs, setWithIngs] = useState(true);
  const nameOf = (i) => {
    if (i.kind === 'text') return i.text;
    const r = recipesById.get(i.recipeId);
    const ko = r?.name || i.name; const z = r?.name_zh || i.name_zh;
    return lang === 'zh' ? (z || ko) : ko;
  };
  const sel = items.filter(i => !off.has(i.id));
  const toggle = (id, on) => setOff(prev => { const n = new Set(prev); if (on) n.delete(id); else n.add(id); return n; });
  return (
    <div data-work-print-dlg style={{ position: 'fixed', inset: 0, zIndex: 450, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 17, fontWeight: 900 }}>🖨️ {Z('작업표 인쇄', '打印工作表')}</div>
        <div style={{ fontSize: 12.5, color: '#71717A', margin: '4px 0 10px' }}>
          {Z('표 한 장에 체크칸·만들 양·재료·시간·이름 칸이 나와요. 빼고 싶은 작업은 체크를 풀어 주세요.', '一张表上有打勾栏、要做的量、用料、时间和签名栏。不想打印的工作请取消勾选。')}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <button type="button" data-work-print-all style={btn('#fff', '#18181B', '#D4D4D8')} onClick={() => setOff(new Set())}>{Z('모두 넣기', '全选')}</button>
          <button type="button" data-work-print-none style={btn('#fff', '#18181B', '#D4D4D8')} onClick={() => setOff(new Set(items.map(i => i.id)))}>{Z('모두 빼기', '全不选')}</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #F4F4F5', borderRadius: 10, padding: '4px 8px' }}>
          {items.map((i, k) => (
            <label key={i.id || k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 2px', borderBottom: '1px dashed #F4F4F5', fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" data-work-print-item={i.id} checked={!off.has(i.id)} onChange={e => toggle(i.id, e.target.checked)}
                style={{ width: 18, height: 18, accentColor: '#EA580C' }} />
              <span style={{ flex: 1, fontWeight: 700, color: off.has(i.id) ? '#A1A1AA' : '#18181B', textDecoration: off.has(i.id) ? 'line-through' : 'none' }}>{nameOf(i)}</span>
              {i.kind === 'recipe' && <span style={{ fontSize: 12, color: '#71717A' }}>{fmtAmount(i.grams, 'g', lang)}</span>}
              {i.done && <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 800 }}>✓</span>}
            </label>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          <input type="checkbox" data-work-print-ings checked={withIngs} onChange={e => setWithIngs(e.target.checked)} style={{ width: 18, height: 18 }} />
          {Z('재료 칸 넣기 (넣으면 가로로 인쇄)', '加上用料栏（会横向打印）')}
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end', alignItems: 'center' }}>
          <span style={{ flex: 1, fontSize: 13, color: '#52525B' }}>{Z(`${sel.length}개 인쇄`, `打印 ${sel.length} 项`)}</span>
          <button type="button" data-work-print-cancel onClick={onCancel} style={btn('#fff', '#52525B', '#D4D4D8')}>{Z('취소', '取消')}</button>
          <button type="button" data-work-print-go disabled={!sel.length} onClick={() => onPrint(sel, withIngs)}
            style={{ ...btn(sel.length ? '#FF6F0F' : '#E4E4E7', '#fff'), cursor: sel.length ? 'pointer' : 'default' }}>🖨️ {Z('인쇄', '打印')}</button>
        </div>
      </div>
    </div>
  );
}

// ── V156 담당자 · 마감일(날짜만) · 내용 칸 (직접 입력 · ✎ 고치기 같이 씀) ─────────────────
function MetaFields({ Z, value, onChange, owners = [], today, compact = false }) {
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div data-work-meta-fields style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: compact ? 6 : 8 }}>
      <FieldRow label={`👤 ${Z('담당', '负责')}`} top>
        <OwnerPicker Z={Z} owners={owners} value={v.owner || ''} onChange={x => set('owner', x)} attr="work" />
      </FieldRow>
      <FieldRow label={`⏰ ${Z('마감', '截止')}`} top>
        <DuePicker Z={Z} today={today} value={v.due || ''} onChange={x => set('due', x)} />
      </FieldRow>
      <FieldRow label={`📝 ${Z('내용', '内容')}`} top>
        <textarea data-work-meta-note value={v.note || ''} maxLength={NOTE_MAX} onChange={e => set('note', e.target.value)} rows={compact ? 2 : 3}
          placeholder={Z('자세한 내용 (선택)', '详细内容（可不填）')} style={textareaStyle} />
      </FieldRow>
    </div>
  );
}

// ── ✎ 항목 고치기 ───────────────────────────────────────────────────────────
function ItemEditor({ Z, btn, it, owners, today, onCancel, onSave }) {
  const [text, setText] = useState(it.kind === 'text' ? (it.text || '') : '');
  const [meta, setMeta] = useState({ note: it.note || '', owner: it.owner || '', due: it.due || '' });
  const isText = it.kind === 'text';
  return (
    <div data-work-editor={it.id} style={{ marginTop: 8, padding: 10, borderRadius: 10, background: '#FFFBF7', border: '1.5px solid #FED7AA' }}>
      {it.from && (
        <div style={{ fontSize: 12, color: MEET.color, fontWeight: 700, marginBottom: 6 }}>
          🗓 {Z('회의에서 온 테스크예요 — 회의에서 고치면 그 내용으로 바뀌어요.', '这是会议里的任务 —— 在会议里修改后会以会议为准。')}
        </div>
      )}
      {isText && (
        <input data-work-edit-text value={text} maxLength={TASK_MAX} onChange={e => setText(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E4E4E7', fontSize: 15, fontWeight: 700 }} />
      )}
      <MetaFields Z={Z} value={meta} onChange={setMeta} owners={owners} today={today} />
      <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
        <button type="button" data-work-edit-cancel onClick={onCancel} style={btn('#fff', '#52525B', '#D4D4D8')}>{Z('취소', '取消')}</button>
        <button type="button" data-work-edit-save onClick={() => onSave({ ...meta, text })} style={btn('#FF6F0F', '#fff')}>{Z('저장', '保存')}</button>
      </div>
    </div>
  );
}

// ── V155 위챗으로 보내기 — 고르고 · 언어 · 넣을 것 → 글 미리보기 → 공유창 / 복사 ──────────
const LS_SHARE = 'semfit_work_share_v1';
const readShareOpts = () => { try { return JSON.parse(localStorage.getItem(LS_SHARE) || '{}') || {}; } catch (e) { return {}; } };

function ShareDialog({ Z, lang, btn, chip, isMobile, items = [], initialOff = [], date, memo, today, recipesById, zhMap, storeName, onClose }) {
  const saved = useMemo(readShareOpts, []);
  const [mode, setMode] = useState(['bi', 'zh', 'ko'].includes(saved.mode) ? saved.mode : 'bi');   // 사장님: 한국어+중국어 같이
  const [withDone, setWithDone] = useState(saved.withDone !== false);
  const [withMeta, setWithMeta] = useState(saved.withMeta !== false);
  const [withIngs, setWithIngs] = useState(!!saved.withIngs);
  const [greeting, setGreeting] = useState('');
  const [off, setOff] = useState(() => new Set(initialOff || []));
  const [sent, setSent] = useState({});   // 몇 번째 글 → 'copied' | 'shared' | 'fail'
  useEffect(() => {
    try { localStorage.setItem(LS_SHARE, JSON.stringify({ mode, withDone, withMeta, withIngs })); } catch (e) { /* 무시 */ }
  }, [mode, withDone, withMeta, withIngs]);

  const sel = items.filter(i => !off.has(i.id));
  const { parts } = useMemo(() => buildWorkText({
    items: sel, recipesById, zhMap, date, today, memo, storeName, greeting, mode, withDone, withMeta, withIngs,
  }), [sel.map(i => i.id).join('|'), recipesById, zhMap, date, today, memo, storeName, greeting, mode, withDone, withMeta, withIngs]); // eslint-disable-line react-hooks/exhaustive-deps
  const sig = parts.join('\n\u0001\n');
  useEffect(() => { setSent({}); }, [sig]);

  const nameOf = (i) => {
    if (i.kind === 'text') return i.text;
    const r = recipesById.get(i.recipeId);
    const ko = r?.name || i.name; const z = r?.name_zh || i.name_zh;
    return lang === 'zh' ? (z || ko) : ko;
  };
  const toggle = (id, on) => setOff(prev => { const n = new Set(prev); if (on) n.delete(id); else n.add(id); return n; });

  const send = async (k) => {
    const r = await shareText(parts[k], isMobile);
    if (r) setSent(st => ({ ...st, [k]: r }));
  };

  const ck = (on, set, label, attr) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
      <input type="checkbox" {...{ [attr]: '' }} checked={on} onChange={e => set(e.target.checked)} style={{ width: 17, height: 17, accentColor: GREEN }} />
      {label}
    </label>
  );
  const leftIds = items.filter(i => !i.done).map(i => i.id);

  return (
    <div data-work-share-dlg style={{ position: 'fixed', inset: 0, zIndex: 450, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}>
      <div style={{ background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : 16, padding: isMobile ? 14 : 20, width: '100%', maxWidth: 560,
                    maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 900 }}>💬 {Z('위챗으로 보내기', '发微信')}</div>
          <button type="button" data-work-share-close onClick={onClose} style={btn('#fff', '#52525B', '#D4D4D8')}>{Z('닫기', '关闭')}</button>
        </div>
        <div style={{ fontSize: 12.5, color: '#71717A', margin: '4px 0 10px', lineHeight: 1.5 }}>
          {isMobile
            ? Z('버튼을 누르면 공유창이 열려요 — 위챗을 고르세요. 공유창이 안 뜨면 글이 복사돼요.', '点按钮会打开分享 —— 选微信。没有分享时会复制文字。')
            : Z('버튼을 누르면 글이 복사돼요 — 위챗 대화창에 붙여넣기(Ctrl+V) 하세요.', '点按钮会复制文字 —— 在微信对话框里粘贴（Ctrl+V）。')}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* 보낼 항목 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 800, flex: 1 }}>{Z(`보낼 항목 ${sel.length} / ${items.length}`, `发送 ${sel.length} / ${items.length} 项`)}</span>
            <button type="button" data-work-share-all onClick={() => setOff(new Set())} style={chip(off.size === 0)}>{Z('모두', '全部')}</button>
            {leftIds.length > 0 && leftIds.length < items.length && (
              <button type="button" data-work-share-left onClick={() => setOff(new Set(items.filter(i => i.done).map(i => i.id)))}
                style={chip(sel.length === leftIds.length && sel.every(i => !i.done))}>{Z('안 한 것만', '只发没做的')}</button>
            )}
            <button type="button" data-work-share-none onClick={() => setOff(new Set(items.map(i => i.id)))} style={chip(false)}>{Z('모두 빼기', '全不选')}</button>
          </div>
          <div style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid #F4F4F5', borderRadius: 10, padding: '2px 8px', marginBottom: 10 }}>
            {items.map((i, k) => (
              <label key={i.id || k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px', borderBottom: '1px dashed #F4F4F5', fontSize: 13.5, cursor: 'pointer' }}>
                <input type="checkbox" data-work-share-item={i.id} checked={!off.has(i.id)} onChange={e => toggle(i.id, e.target.checked)}
                  style={{ width: 17, height: 17, accentColor: GREEN }} />
                <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: off.has(i.id) ? '#A1A1AA' : '#18181B', textDecoration: off.has(i.id) ? 'line-through' : 'none',
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(i)}</span>
                {i.from && <span style={{ fontSize: 11, fontWeight: 800, color: MEET.color }}>🗓</span>}
                {i.owner && <span style={{ fontSize: 11.5, color: '#1A56A0', fontWeight: 700, whiteSpace: 'nowrap' }}>👤 {i.owner}</span>}
                {i.done && <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 800 }}>✓</span>}
              </label>
            ))}
          </div>

          {/* 언어 · 넣을 것 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: '#52525B', minWidth: 40 }}>{Z('언어', '语言')}</span>
            {[['bi', '한국어 + 中文'], ['zh', '中文'], ['ko', '한국어']].map(([k, l]) => (
              <button key={k} type="button" data-work-share-mode={k} onClick={() => setMode(k)}
                style={{ ...chip(mode === k), ...(mode === k ? { borderColor: GREEN, background: '#F0FDF4', color: '#15803D' } : {}) }}>{l}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
            {ck(withDone, setWithDone, Z('완료 표시 ✅⬜', '完成标记 ✅⬜'), 'data-work-share-done')}
            {ck(withMeta, setWithMeta, Z('내용 · 담당 · 기한', '内容 · 负责 · 截止'), 'data-work-share-meta')}
            {ck(withIngs, setWithIngs, Z('레시피 재료', '配方用料'), 'data-work-share-ings')}
          </div>
          <input data-work-share-greeting value={greeting} maxLength={200} onChange={e => setGreeting(e.target.value)}
            placeholder={Z('맨 위에 붙일 말 (선택) — 예: 오늘 11시까지 부탁해요', '放在最上面的话（可不填）—— 例：今天11点前完成')}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 9, border: '1.5px solid #E4E4E7', fontSize: 13.5, marginBottom: 10 }} />

          {/* 미리보기 + 보내기 */}
          {sel.length === 0 ? (
            <div style={{ fontSize: 13, color: '#B45309', padding: '10px 0' }}>{Z('보낼 항목을 하나 이상 골라 주세요.', '请至少选一项。')}</div>
          ) : parts.map((text, k) => (
            <div key={k} style={{ marginBottom: 12 }}>
              {parts.length > 1 && (
                <div style={{ fontSize: 12, fontWeight: 700, color: '#B45309', marginBottom: 4 }}>
                  {Z(`길어서 ${parts.length}번에 나눠 보내요 (${k + 1}/${parts.length})`, `内容较长，分 ${parts.length} 次发送（${k + 1}/${parts.length}）`)}
                </div>
              )}
              <pre data-work-share-text={k} style={{ margin: 0, padding: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, fontSize: 12.5,
                                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflowY: 'auto', fontFamily: 'inherit',
                                                    userSelect: 'text', WebkitUserSelect: 'text', lineHeight: 1.55 }}>{text}</pre>
              <button type="button" data-work-share-send={k} onClick={() => send(k)}
                style={{ ...btn(GREEN, '#fff'), width: '100%', marginTop: 6, padding: '12px', fontSize: 15 }}>
                💬 {Z('위챗으로 보내기', '发微信')}{parts.length > 1 ? ` (${k + 1}/${parts.length})` : ''}
              </button>
              {sent[k] && (
                <div data-work-share-state={sent[k]} style={{ fontSize: 12.5, marginTop: 5, fontWeight: 700, color: sent[k] === 'fail' ? '#B91C1C' : '#15803D' }}>
                  {sent[k] === 'copied' ? Z('✅ 복사됐어요 — 위챗 대화창에 붙여넣으세요', '✅ 已复制，请粘贴到微信对话框')
                    : sent[k] === 'shared' ? Z('✅ 공유창으로 보냈어요', '✅ 已发送')
                    : Z('⚠ 복사가 안 됐어요 — 위 글을 길게 눌러 복사하세요', '⚠ 复制失败，请长按上面的文字复制')}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 저장한 내역 ─────────────────────────────────────────────────────────────
function HistoryPane({ history = [], lang, Z, card, btn, isMobile, recipesById, hasToday, onBack, onLoad, onDelete, onPrint, onShare, onUndo }) {
  const [openId, setOpenId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const sorted = useMemo(() => [...(history || [])].sort((a, b) =>
    String(b.date || '').localeCompare(String(a.date || '')) || (Number(b.savedAt) || 0) - (Number(a.savedAt) || 0)), [history]);
  const nameOf = (i) => {
    if (i.kind === 'text') return i.text;
    const r = recipesById.get(i.recipeId);
    const ko = r?.name || i.name; const z = r?.name_zh || i.name_zh;
    return lang === 'zh' ? (z || ko) : ko;
  };
  return (
    <div data-work-history>
      <div style={{ ...card, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" data-work-history-back onClick={onBack} style={btn('#fff', '#18181B', '#D4D4D8')}>← {Z('오늘 목록', '今日清单')}</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 900 }}>📅 {Z('작업 내역', '工作记录')}</div>
        <div style={{ fontSize: 12, color: '#71717A' }}>{Z(`${sorted.length}건`, `${sorted.length} 条`)}</div>
      </div>
      {sorted.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: '#71717A', padding: '30px 16px', fontSize: 13 }}>
          {Z('아직 저장한 작업이 없어요. 오늘 목록에서 💾 저장을 누르면 여기에 날짜별로 쌓여요.', '还没有保存的工作。在今日清单点 💾 保存，就会按日期记在这里。')}
        </div>
      )}
      {sorted.map(e => {
        const open = openId === e.id;
        const list = e.items || [];
        const done = list.filter(i => i.done).length;
        return (
          <div key={e.id} data-work-history-entry={e.id} style={{ ...card, marginBottom: 8, padding: 0, overflow: 'hidden' }}>
            <button type="button" data-work-history-toggle={e.id} onClick={() => setOpenId(open ? null : e.id)}
              style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: isMobile ? '12px' : '12px 16px', cursor: 'pointer' }}>
              <div style={{ minWidth: isMobile ? 92 : 130 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#18181B' }}>{fmtDay(e.date, lang)}</div>
                <div style={{ fontSize: 11, color: '#A1A1AA' }}>{e.savedBy || ''}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#52525B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {list.slice(0, 4).map(nameOf).join(' · ')}{list.length > 4 ? ` ${Z(`외 ${list.length - 4}`, `等 ${list.length} 项`)}` : ''}
                {e.memo ? <div style={{ fontSize: 12, color: '#A1A1AA', overflow: 'hidden', textOverflow: 'ellipsis' }}>📝 {e.memo}</div> : null}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', color: done === list.length ? '#16A34A' : '#B45309' }}>
                {done}/{list.length}
              </div>
              <span style={{ color: '#A1A1AA' }}>{open ? '▾' : '▸'}</span>
            </button>
            {open && (
              <div style={{ borderTop: '1px solid #F4F4F5', padding: isMobile ? '10px 12px 12px' : '10px 16px 14px' }}>
                {list.map((i, k) => {
                  const due = fmtDue(i, lang, '');
                  return (
                    <div key={i.id || k} data-work-history-item style={{ padding: '5px 0', borderBottom: '1px dashed #F4F4F5', fontSize: 13 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ width: 22, textAlign: 'center', fontWeight: 900, color: i.done ? '#16A34A' : '#D4D4D8' }}>{i.done ? '✓' : '○'}</span>
                        <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: i.done ? '#18181B' : '#71717A' }}>
                          {nameOf(i)}
                          {i.from && (
                            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: MEET.color, background: MEET.bg, borderRadius: 6, padding: '1px 6px' }}>
                              🗓 {i.from.title || Z('회의', '会议')}
                            </span>
                          )}
                        </span>
                        {i.kind === 'recipe' && <span style={{ fontWeight: 700 }}>{fmtAmount(i.grams, 'g', lang)}</span>}
                        {i.done && i.at ? <span style={{ fontSize: 11, color: '#A1A1AA', minWidth: 80, textAlign: 'right' }}>{hhmm(i.at)}{i.by ? ` ${i.by}` : ''}</span> : null}
                        {i.pending && onUndo && (
                          <button type="button" data-work-history-undo={i.id} onClick={() => onUndo(i.id)} title={Z('체크를 풀고 오늘의 작업으로 되돌려요', '取消打勾，回到今日工作')}
                            style={{ border: '1.5px solid #D4D4D8', background: '#fff', borderRadius: 8, padding: '2px 7px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', color: '#52525B', whiteSpace: 'nowrap' }}>↩ {Z('되돌리기', '撤回')}</button>
                        )}
                      </div>
                      {(i.owner || due || i.note) && (
                        <div data-work-history-meta style={{ marginLeft: 30, marginTop: 2, fontSize: 12, color: '#52525B', lineHeight: 1.5 }}>
                          {[i.owner ? `👤 ${i.owner}` : '', due ? `⏰ ${due}${Z('까지', '前')}` : ''].filter(Boolean).join(' · ')}
                          {i.note && <div style={{ whiteSpace: 'pre-wrap', color: '#71717A' }}>📝 {i.note}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {e.memo && <div style={{ marginTop: 8, fontSize: 13, color: '#52525B', whiteSpace: 'pre-wrap' }}>📝 {e.memo}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  <button type="button" data-work-history-load={e.id} onClick={() => onLoad(e, false)} style={btn('#FF6F0F', '#fff')}>
                    📋 {hasToday ? Z('오늘 목록에 더하기', '加到今日清单') : Z('오늘 목록으로 불러오기', '调到今日清单')}
                  </button>
                  {hasToday && (
                    <button type="button" data-work-history-replace={e.id} onClick={() => onLoad(e, true)} style={btn('#fff', '#C2410C', '#FDBA74')}>
                      🔁 {Z('오늘 목록을 이걸로 바꾸기', '用这张替换今日清单')}
                    </button>
                  )}
                  <button type="button" data-work-history-share={e.id} onClick={() => onShare && onShare(e)} style={btn('#07C160', '#fff')}>💬 {Z('위챗', '微信')}</button>
                  <button type="button" data-work-history-print={e.id} onClick={() => onPrint(e)} style={btn('#fff', '#18181B', '#D4D4D8')}>🖨️ {Z('인쇄', '打印')}</button>
                  <div style={{ flex: 1 }} />
                  {list.some(i => i.pending) ? (
                    <span style={{ fontSize: 11.5, color: '#A1A1AA', alignSelf: 'center' }}>{Z('오늘 체크한 일은 ↩ 되돌리기로 뺄 수 있어요', '今天打勾的可以用 ↩ 撤回')}</span>
                  ) : confirmDel === e.id ? (
                    <>
                      <button type="button" data-work-history-delete-confirm={e.id} onClick={() => { setConfirmDel(null); setOpenId(null); onDelete(e.id); }} style={btn('#DC2626', '#fff')}>{Z('정말 지우기', '确定删除')}</button>
                      <button type="button" onClick={() => setConfirmDel(null)} style={btn('#fff', '#52525B', '#D4D4D8')}>{Z('취소', '取消')}</button>
                    </>
                  ) : (
                    <button type="button" data-work-history-delete={e.id} onClick={() => setConfirmDel(e.id)} style={btn('#fff', '#DC2626', '#FECACA')}>🗑️ {Z('삭제', '删除')}</button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
